import { Assessment, Gavel, Description, Chat } from '@mui/icons-material';
import { alpha, Box, Typography, Card, CardContent, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import ApplicationActions from '../components/applicationActions';
import InitialDocuments from '../components/initialDocuments';
import ApplicationProgressStepper from '../components/visaStepper';
import PageBackground from 'components/v2/PageBackground';
import NoApplicationScreen from 'views/v2/applicant/noApplication/components/noApplicationScreen';

import PERMISSION from 'assets/permissions.json';
import useActiveApplication from 'hooks/useActiveApplication';
import { isCompletedAssessmentUnset, useCompletedForms } from 'hooks/useCompletedForms';
import { RootState } from 'redux/store';
import { isAllDocumentsCompleted } from 'utils/v2/document';
import { isEqualString } from 'utils/v2/text';

const Dashboard = () => {
    const user = useSelector((state: RootState) => state.user.entity);
    const { permissionsList } = useSelector((state: RootState) => state.permissions);
    const { list: documents } = useSelector((state: RootState) => state.documentsApp);

    const { t } = useTranslation();
    const { selectedApplicationId, applications, loading: appsLoading } = useActiveApplication();

    const application = applications?.find((app) => isEqualString(app.id, selectedApplicationId));

    const isAllDocumentsCompletedFlag =
        (documents ?? []).length > 0 && isAllDocumentsCompleted(documents || []);

    const hasEditProfileAccess = permissionsList.some(
        (permission) => permission?.name === PERMISSION?.EDIT
    );
    const hasViewAssessmentResult = permissionsList.some(
        (permission) => permission?.name === PERMISSION?.VIEW
    );
    const hasCompletedAssessment = !isCompletedAssessmentUnset(user?.completed_assessment);
    // Fetch completed forms for overview
    useCompletedForms(user?.id);

    // Handle application selection with section
    // const handleApplicationNavigate = (applicationId: number, section: string) => {
    //   setActiveApplication(applicationId);
    //   navigate(`/visa-process/${applicationId}/${section}`);
    // };

    // Action cards for the selected application
    const actionCards = (selectedAppId?: number | null) => {
        const basePath = selectedAppId ? `/visa-process/${selectedAppId}` : '/visa-process';

        return [
            ...(hasEditProfileAccess
                ? [
                      {
                          icon: <Assessment />,
                          title: t(
                              hasCompletedAssessment
                                  ? 'assessmentCompleted'
                                  : 'startAssessmentTitle'
                          ),
                          description: t(
                              hasCompletedAssessment
                                  ? 'completedAssessmentDescription'
                                  : 'startAssessmentDescription'
                          ),
                          path: '/visa-process/assessment',
                          isCompleted: hasCompletedAssessment,
                      },
                  ]
                : []),
            {
                icon: <Gavel />,
                title: t('agreement'),
                description: t('consultationAgreementDescription'),
                path: `${basePath}/agreement`,
                isCompleted: false,
            },
            {
                icon: <Description />,
                title: t('uploadDocumentsTitle'),
                description: t('uploadDocumentsDescription'),
                path: `${basePath}/documents`,
                isCompleted: false,
            },
            ...(hasViewAssessmentResult
                ? [
                      {
                          icon: <Assessment />,
                          title: t('assessmentResultTitle'),
                          description: t('assessmentResultDescription'),
                          path: `${basePath}/results`,
                          isCompleted: false,
                      },
                  ]
                : []),
            {
                icon: <Chat />,
                title: t('chat'),
                description: t('chatDescription'),
                path: `${basePath}/chats`,
                isCompleted: false,
            },
        ];
    };

    if (!appsLoading && (!applications || applications.length === 0)) {
        return <NoApplicationScreen />;
    }

    return (
        <PageBackground rootSx={{ overflow: 'auto' }}>
            {/* Welcome Header */}
            <Box sx={{ mb: 2.5 }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontSize: { xs: '1.3rem', xl: '1.75rem' },
                        fontWeight: 700,
                    }}
                >
                    {t('welcomeGreetingDashboard', { name: user?.firstname })}
                </Typography>
            </Box>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <ApplicationProgressStepper
                        application={application}
                        isAllDocumentsCompletedFlag={isAllDocumentsCompletedFlag}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                    <InitialDocuments />
                </Grid>
            </Grid>

            {selectedApplicationId && (
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5, fontSize: '0.875rem' }}
                    >
                        Quick Actions
                    </Typography>
                    <Grid container spacing={2}>
                        {actionCards(selectedApplicationId).map((card, index) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                <Box
                                    component={Link}
                                    to={card.path}
                                    sx={{ textDecoration: 'none' }}
                                >
                                    <Card
                                        sx={(theme) => ({
                                            height: '100%',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            border: '1px solid',
                                            borderColor: card.isCompleted
                                                ? 'success.main'
                                                : 'divider',
                                            bgcolor: card.isCompleted
                                                ? alpha(theme.palette.success.main, 0.05)
                                                : 'background.paper',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 2,
                                                borderColor: card.isCompleted
                                                    ? 'success.dark'
                                                    : 'primary.light',
                                            },
                                        })}
                                    >
                                        <CardContent
                                            sx={{
                                                ':last-child': { pb: { xs: 1.5, xl: 2.5 } },
                                                px: { xs: 1.75, xl: 2.5 },
                                                py: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={(theme) => ({
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 1.5,
                                                    bgcolor: card.isCompleted
                                                        ? alpha(theme.palette.success.main, 0.12)
                                                        : 'rgba(102, 126, 234, 0.08)',
                                                    color: card.isCompleted
                                                        ? 'success.main'
                                                        : 'text.primary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                })}
                                            >
                                                {card.icon}
                                            </Box>
                                            <Typography
                                                variant="subtitle1"
                                                gutterBottom
                                                sx={{ fontWeight: 700, fontSize: '0.938rem' }}
                                            >
                                                {card.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.813rem' }}
                                            >
                                                {card.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* All Applications Section */}
            {applications && applications.length > 1 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                        All Applications
                    </Typography>
                    <Grid container spacing={2}>
                        <ApplicationActions />
                    </Grid>
                </Box>
            )}
        </PageBackground>
    );
};

export default Dashboard;
